import {
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON,
  DepositCaptureStrategy,
  type DepositCaptureStrategyFallbackReason,
} from '@principle-theorem/principle-core/interfaces';
import { type Timestamp } from '@principle-theorem/shared';
import { duration } from 'moment-timezone';
import { DEPOSIT_REQUEST_HOLD_MS } from './deposit-request';

export const DEPOSIT_FORFEITURE_RUNWAY = duration(24, 'hours');

/**
 * Resolves the strategy that can actually be honoured for this booking.
 *
 * Two configured strategies cannot always be carried out. Capturing on
 * approval needs a booking request for someone to approve, which an
 * appointment booked straight into the diary never has. Cancellation cover
 * needs its forfeiture runway to fit inside the card authorisation window, or
 * the hold lapses before it can protect the appointment. Either way the money
 * is taken up front instead, so a hold is never left with nothing able to
 * capture it.
 *
 * `hasApprovableBookingRequest` asks whether a booking request exists for staff
 * to accept, which is not the same as whether the patient booked online: staff
 * can raise a deposit against a booking request themselves, and that request is
 * still approvable.
 */
export function resolveEffectiveCaptureStrategy(params: {
  requestedStrategy: DepositCaptureStrategy;
  appointmentStart?: Timestamp;
  hasApprovableBookingRequest: boolean;
  nowMillis?: number;
}): {
  strategy: DepositCaptureStrategy;
  fallbackReason?: DepositCaptureStrategyFallbackReason;
} {
  if (
    !params.hasApprovableBookingRequest &&
    params.requestedStrategy ===
      DepositCaptureStrategy.CaptureOnBookingRequestApproval
  ) {
    return {
      strategy: DepositCaptureStrategy.ChargeOnBookingRequest,
      fallbackReason:
        DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON.NoBookingRequestToApprove,
    };
  }

  if (
    params.requestedStrategy !==
    DepositCaptureStrategy.CaptureOnCancellationOrReschedule
  ) {
    return { strategy: params.requestedStrategy };
  }

  const fallbackStrategy = params.hasApprovableBookingRequest
    ? DepositCaptureStrategy.CaptureOnBookingRequestApproval
    : DepositCaptureStrategy.ChargeOnBookingRequest;

  if (!params.appointmentStart) {
    return {
      strategy: fallbackStrategy,
      fallbackReason:
        DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON.AppointmentStartUnknown,
    };
  }

  const nowMillis = params.nowMillis ?? Date.now();
  const forfeitureRunwayEndMillis =
    params.appointmentStart.toMillis() +
    DEPOSIT_FORFEITURE_RUNWAY.asMilliseconds();

  if (forfeitureRunwayEndMillis >= nowMillis + DEPOSIT_REQUEST_HOLD_MS) {
    return {
      strategy: fallbackStrategy,
      fallbackReason:
        DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON.AuthorisationWindowTooShort,
    };
  }

  return { strategy: params.requestedStrategy };
}
